import { z } from 'zod';
import { Error } from '@feature/shared';
import type { Store } from '@toolkit/store-generator';

type Issues = Record<string, string>;

/**
 * Main validation caller function.
 * Handles schema validation, error processing, and store interactions.
 *
 * @param schema - The Zod schema to validate the data against.
 * @param data - The data to validate.
 * @param store - The store to save errors into if validation fails.
 * @returns The validated data on success.
 * @throws Validation errors or unexpected exceptions.
 */
const validate = <T>(schema: z.ZodSchema<T>, data: unknown, form: Store<Issues>): Issues => {
  try {
    clearValidationState(form);
    schema.parse(data);
    return {}; // No validation issues
  } catch (error) {
    if (error instanceof z.ZodError) {
      const transformedErrors = handleZodError(error);
      save(form, transformedErrors);
      console.error('Validation failed:', transformedErrors);
      return transformedErrors;
    } else {
      console.error('Unexpected error:', error);
      return { _global: 'An unexpected error occurred' };
    }
  }
};

/**
 * Clears all previous validation errors from the store and shared error state.
 *
 * @param store - The store to clear errors from.
 */
const clearValidationState = <T>(form: Store<T>): void => {
  Error.clearError();
  clear(form);
};

/**
 * Processes validation errors, stores them, and updates global error state.
 *
 * @param error - The error object to process.
 * @param store - The store to save errors into if validation fails.
 */
const processValidationError = (error: unknown, form: Store<Issues>) => {
  Error.setPermenantError(
    'There have been validation errors, please check all details entered are correct.'
  );

  if (error instanceof z.ZodError) {
    const transformedErrors = handleZodError(error);
    save(form, transformedErrors); // Write error map to the store
    console.error('Validation failed:', transformedErrors);
  } else {
    console.error('Unexpected error:', error);
  }
};

/**
 * Converts a ZodError into a map of field error messages.
 *
 * @param error - The error object to be handled, expected to be a ZodError.
 * @returns An object where keys are field paths and values are error messages.
 */
const handleZodError = (error: z.ZodError): Issues => {
  return error.issues.reduce((acc: Issues, issue) => {
    acc[issue.path.join('.')] = issue.message;
    return acc;
  }, {});
};

/**
 * Writes data to the store.
 *
 * @param store - The store to write the data into.
 * @param data - The data to be written into the store.
 */
const save = <T>(form: Store<T>, data: T): void => {
  form.set(data);
};

/**
 * Clears the store of its current data.
 *
 * @param store - The store to clear.
 */
const clear = <T>(form: Store<T>): void => {
  form.clear();
};

export {
  validate,
  clearValidationState,
  processValidationError,
  handleZodError,
  save,
  clear,
  type Issues
};
